/* ==========================================================================
   BC Cart Drawer — keep the footer in step with the line items.

   THE PROBLEM. theme.js's CartForm.cartMarkup() replaces [data-products] and
   [data-discounts] and sets [data-subtotal], and nothing else. The drawer's
   "Subtotal (N items)" label and its struck compare-at total are rendered in
   Liquid, so on a page where the cart later changes they keep the values the
   page was built with — the count reads "(0 items)" after the first add, and
   the struck total never moves.

   THE FIX, WITHOUT TOUCHING theme.js. templates/cart.ajax.liquid stamps
   data-count, data-cart-subtotal and data-cart-compare-total onto the
   .cart__items element it returns, and cartMarkup() appends that element
   wholesale. So a childList observer on [data-products] sees every rebuild
   and can read the authoritative numbers straight off it.

   theme.js is deliberately left alone: it is the file Isaac's open branches
   also touch, and a clean git merge of two edits to the same file is exactly
   how this project has lost code before.

   Deleted or folded into the drawer's own code at stage 10 only if the
   drawer stops using theme.CartForm; until then it is load-bearing.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '.bc-drawer';
  var PRODUCTS = '[data-products]';
  var ITEMS = '.cart__items';

  function root() { return document.querySelector(ROOT); }

  /* Pluralisation comes from the locale file via theme.strings, so the drawer
     says "1 item" and "2 items" in whatever language is serving. Falls back to
     the English shape only if the string is missing, which would mean the
     locale key was never added. */
  function countLabel(count) {
    var s = window.theme && theme.strings;
    var tpl = null;
    if (s) {
      if (count === 1 && s.cartSubtotalCountOne) tpl = s.cartSubtotalCountOne;
      else if (s.cartSubtotalCountOther) tpl = s.cartSubtotalCountOther;
    }
    if (!tpl) tpl = count === 1 ? 'Subtotal ({{ count }} item)' : 'Subtotal ({{ count }} items)';
    return tpl.replace(/\{\{\s*count\s*\}\}/g, count);
  }

  function sync() {
    var el = root();
    if (!el) return;

    var items = el.querySelector(PRODUCTS + ' ' + ITEMS);
    if (!items) return;

    var count = parseInt(items.getAttribute('data-count'), 10);
    var subtotal = parseInt(items.getAttribute('data-cart-subtotal'), 10);
    var compare = parseInt(items.getAttribute('data-cart-compare-total'), 10);

    /* Parse before testing, not after. "undefined" is a truthy string, so a
       `||` fallback on the raw attribute could never fire — the same trap that
       produced the £NaN in Your Setup (#334). */
    if (isFinite(count)) {
      var label = el.querySelector('.bc-drawer__subtotal-label');
      if (label) label.textContent = countLabel(count);
      el.classList.toggle('is-empty', count === 0);
    }

    /* ⚠ THE FOOTER AMOUNTS ARE LIQUID'S, NEVER FORMATTED HERE. They used to
       go through theme.Currency.formatMoney with the PAGE's money format, and
       theme.js still writes the subtotal that way. When the basket's currency
       differs from the page's — a GB basket just switched to /fr-fr/ — that
       printed a GBP total with a euro sign, "17.277 €" under £ lines (Stage
       7, 28 September). The ajax template stamps both amounts through the
       money filter, the same filter the lines use, so the footer always
       agrees with the rows. Overwrites theme.js's subtotal: this observer
       runs after cartMarkup() has finished. */
    var subtotalText = items.getAttribute('data-cart-subtotal-money');
    var compareText = items.getAttribute('data-cart-compare-money');
    var nowEl = el.querySelector('.bc-drawer__subtotal-now[data-subtotal]');
    if (nowEl && subtotalText) nowEl.innerHTML = subtotalText;

    var wasEl = el.querySelector('[data-bc-compare-total]');
    var values = el.querySelector('.bc-drawer__subtotal-values');

    /* Only show the struck figure when it is genuinely higher, so a basket at
       full price shows one number rather than the same number struck through
       beside itself. The element is created and removed rather than hidden,
       because Liquid only emits it when it applies and the two paths must
       agree. */
    if (isFinite(compare) && isFinite(subtotal) && compare > subtotal && compareText) {
      if (!wasEl && values) {
        wasEl = document.createElement('s');
        wasEl.className = 'bc-drawer__subtotal-was';
        wasEl.setAttribute('data-bc-compare-total', '');
        values.appendChild(wasEl);
      }
      if (wasEl) wasEl.innerHTML = compareText;
    } else if (wasEl) {
      wasEl.parentNode.removeChild(wasEl);
    }

    /* ⚠ THE SHARED REFRESH TRIGGER. One event, every consumer.

       theme.js's buildCart() is the single fetch per cart change: it requests
       /cart?view=ajax once and replaces [data-products]. The observer above
       fires on exactly that swap, which makes it the one reliable "the cart
       changed and the DOM now reflects it" moment available without editing
       theme.js — and it covers every route in, including adds from the buy row
       and from collection quick-add, which both go through ajaxProduct:added.

       The cart drawer FBT (bc-cart-fbt.js) re-renders on this and keeps its
       own ?section_id= fetch. So: one trigger, two consumers, and
       still one cart fetch — the FBT's is for different markup, not a second
       request for the same data.

       Deliberately dispatched AFTER the footer is in step, so a listener
       reading the drawer sees consistent numbers. */
    document.dispatchEvent(new CustomEvent('bc:cart:updated', {
      detail: { count: count, subtotal: subtotal, compareTotal: compare }
    }));
  }

  /* ==========================================================================
     WALLET BUTTONS — move, never render a second copy.

     content_for_additional_checkout_buttons may be output once per page and
     layout/theme.liquid already outputs it, parked and hidden. Shopify's
     portable-wallets script injects the real button elements into that copy at
     runtime; they are not in the server HTML. Rendering our own second copy in
     the drawer was the first attempt and produced buttons at 123x42 with
     visibility:hidden and &nbsp; inside — what reveals them is being
     RE-PARENTED into a visible container, not merely sitting in one.

     So we do what Rebuy does, read from its own SmartCartV2 bundle:
     appendChild each button into our container (a move, not a clone), hide the
     source, set a populated flag so it never runs twice.
     ========================================================================== */

  var WALLETS = [
    'shop-pay-wallet-button',
    'shopify-apple-pay-button',
    'shopify-google-pay-button',
    'shopify-amazon-pay-button',
    'shopify-paypal-button'
  ].join(',');
  var SOURCE = '.additional-checkout-buttons';
  var TARGET = '[data-bc-wallets]';

  var walletsStarted = false;
  var walletObserver = null;
  var walletTimer = null;

  function stopWaiting() {
    if (walletObserver) { walletObserver.disconnect(); walletObserver = null; }
    if (walletTimer) { clearTimeout(walletTimer); walletTimer = null; }
  }

  /* ⚠ THIS RUNS REPEATEDLY, AND MUST. The first version stopped watching the
     moment it moved anything, and that is why Google Pay never appeared in our
     drawer while it appears in Rebuy's: Shopify injects each wallet only once
     its own availability check resolves, and Google Pay's resolves later than
     Shop Pay's and PayPal's. We had already disconnected by then.

     Rebuy does not stop either — read from its SmartCartV2 bundle, it starts a
     setInterval when the buttons are not there yet and keeps retrying. It also
     harvests far later in the page lifecycle (rebuy.js loads on first
     interaction, then the bundle, then Vue mounts), by which point more of
     those checks have completed. We open at the click itself, so we have to
     keep looking.

     Repeat calls cannot double-move: the buttons are MOVED out of the source,
     so a later query of the source returns only new arrivals. That is also
     what guarantees exactly one set on the page — a node can only be in one
     place. The <shopify-accelerated-checkout-cart> element is deliberately
     left behind, because it is Shopify's injection point for the late ones. */
  function harvestWallets() {
    var target = document.querySelector(TARGET);
    var source = document.querySelector(SOURCE);
    if (!target || !source) return;

    var buttons = source.querySelectorAll(WALLETS);

    /* Nothing new. Return without touching anything — moving the
       <shopify-accelerated-checkout-cart> shell before Shopify has filled it
       would relocate the injection point and leave nowhere for the real
       buttons to appear. Waiting is the whole point of this branch. */
    if (!buttons.length) return;

    Array.prototype.forEach.call(buttons, function (btn) {
      target.appendChild(btn);
    });

    source.style.display = 'none';
    target.hidden = false;

    /* Late arrivals go through the same measured pass as the first set, so a
       wallet that renders nothing still takes no space whenever it turns up. */
    settleWallets(target);
  }

  /* ⚠ A WALLET THAT RENDERS NOTHING MUST TAKE NO SPACE.
     Each moved button is a grid row, so a slot that never paints still
     reserves one — seen on device as a large blank gap between Apple Pay and
     Google Pay, almost certainly an unrendered PayPal.

     Measured rather than assumed, because "did this render" has no reliable
     declarative test: shop-pay-wallet-button reports no shadow root, so
     :empty and shadowRoot checks both lie. Height is the honest signal.

     Polled rather than read once, because the reveal is exactly what the move
     triggers — reading immediately would mark every button as empty. Each pass
     promotes anything that has gained height; the last pass condemns whatever
     still has none. A button promoted early is never re-tested. */
  function settleWallets(target) {
    var PASSES = [120, 400, 1200, 3000];

    function pass(i) {
      /* ⚠ NEVER CONDEMN WHILE THE CONTAINER ITSELF IS NOT RENDERED. Below 768px
         the wallets are display:none by design (#339, Checkout only), so every
         child measures zero height. Condemning them there would attach
         .bc-wallet--empty permanently, and they would then stay hidden even if
         the viewport later grew past the breakpoint — a tablet rotating to
         landscape, or a desktop window being widened. Skip the pass entirely;
         nothing is shown either way while the container is hidden. */
      if (!target.getClientRects().length) {
        if (i < PASSES.length - 1) setTimeout(function () { pass(i + 1); }, PASSES[i + 1] - PASSES[i]);
        return;
      }

      var kids = target.children;
      for (var k = 0; k < kids.length; k++) {
        var el = kids[k];
        if (el.classList.contains('bc-wallet--ready')) continue;
        if (el.getBoundingClientRect().height > 0) {
          el.classList.add('bc-wallet--ready');
          el.classList.remove('bc-wallet--empty');
        } else if (i === PASSES.length - 1) {
          el.classList.add('bc-wallet--empty');
        }
      }
      if (i < PASSES.length - 1) setTimeout(function () { pass(i + 1); }, PASSES[i + 1] - PASSES[i]);
    }

    setTimeout(function () { pass(0); }, PASSES[0]);
  }

  function waitForWallets() {
    var source = document.querySelector(SOURCE);
    if (!source) return;

    /* Every open, not just the first: a free catch-up on anything that has
       appeared since, which costs one querySelectorAll and needs no timers. */
    harvestWallets();

    /* The observer window opens once and is bounded. Reopening the drawer does
       not restart it — the catch-up above covers that case without leaving an
       observer alive for the life of the page. */
    if (walletsStarted) return;
    walletsStarted = true;

    /* Two independent triggers, because neither alone is reliable: a button may
       be injected before its custom element upgrades, or after. */
    walletObserver = new MutationObserver(harvestWallets);
    walletObserver.observe(source, { childList: true, subtree: true });

    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('shop-pay-wallet-button').then(harvestWallets).catch(function () {});
    }

    /* Bounded so a store with no wallets configured does not leave an observer
       running for the life of the page. Nothing is shown if it expires — the
       target stays hidden and the drawer keeps Checkout and View Cart. */
    walletTimer = setTimeout(stopWaiting, 15000);
  }

  function start() {
    var el = root();
    if (!el) return;
    var products = el.querySelector(PRODUCTS);
    if (!products) return;

    sync();

    /* childList on the products container only. cartMarkup() empties it and
       appends a fresh .cart__items, so every rebuild is one mutation record
       here — no need to watch the whole drawer, and no risk of reacting to
       our own footer edits. */
    new MutationObserver(sync).observe(products, { childList: true });

    /* Harvest on open, not on load: the move only reveals the buttons if the
       destination is visible, and #HeaderCart is display:none until then.
       theme.js dispatches CartDrawer:open from HeaderCart.open(). The populated
       flag makes every later open a no-op, so close-and-reopen costs nothing
       and the buttons simply stay where they were put. */
    document.addEventListener('CartDrawer:open', waitForWallets);

    /* ======================================================================
       STAGE 6 — refused ADDS.

       theme.AjaxProduct already does the right thing for the buy row: it reads
       Shopify's `description`, appends it to the product form, and dispatches
       ajaxProduct:error carrying the same text. That event is the feed here —
       the message is not re-derived, it is the one theme.js already wrote.

       WHAT THIS CAN AND CANNOT DO, because it would be easy to expect more of
       it than it delivers: a REFUSED add never opens the drawer, since
       theme.js only opens on ajaxProduct:added. So this surface is read when
       the drawer is ALREADY open — which is the drawer FBT's own add, the one
       case that had nowhere to report at all. For a refused add on the PDP the
       shopper is looking at the button, and the message that matters is the
       one beside it. Both are written; neither is a substitute for the other.
       ====================================================================== */
    document.addEventListener('ajaxProduct:error', function (evt) {
      var message = evt && evt.detail && evt.detail.errorMessage;
      refusedAdd(message || genericError(), function () {
        /* theme.js appends its copy to the form as div.errors and the event
           does not say which form, so find it by the text theme.js wrote
           from this same message. */
        var written = document.querySelectorAll('form .errors');
        for (var i = 0; i < written.length; i++) {
          if (written[i].textContent === message) written[i].parentNode.removeChild(written[i]);
        }
      });
    });

    /* Any successful change clears it. Both events are dispatched after the
       cart has actually moved, so the message can never outlive the state that
       produced it — except a partial add's, which is written by this very
       rebuild and so is shown here instead of cleared. */
    document.addEventListener('bc:cart:updated', function () {
      if (pendingMessage) {
        showError(pendingMessage);
        pendingMessage = null;
      } else {
        clearError();
      }
    });
    document.addEventListener('ajaxProduct:added', clearError);

    /* A message lasts one opening. Stage 7 found the last one still showing
       when the drawer was next opened, long after it stopped being true. A
       partial add's message survives the opening it causes, because it is
       written after the rebuild, which finishes after CartDrawer:open. */
    document.addEventListener('CartDrawer:open', clearError);

    startKeyboard();
    groupFanFigure();
  }

  /* ==========================================================================
     FANS STRIP — thousands separators for the follower figure.

     Liquid has already ROUNDED THE FIGURE DOWN to the nearest thousand and
     printed it as plain digits ("46000") in its own span; that is the only
     place the rounding happens. Liquid cannot group digits for a language,
     so this does: 46,000 in English, 46.000 in German and Spanish, 46 000 in
     French and Portuguese. Nothing is rounded here, so it cannot overstate.

     "en" is read as en-GB, the store's English. Runs once: the strip is
     outside everything a cart change rebuilds, and only the figure's own
     span is touched — the sentence and the names beside it are Liquid's.
     ========================================================================== */
  function groupFanFigure() {
    var figure = document.querySelector('[data-bc-fans] [data-bc-fans-figure]');
    if (!figure || !window.Intl || !Intl.NumberFormat) return;

    var followers = parseInt(figure.textContent, 10);
    if (!isFinite(followers) || followers <= 0) return;

    var locale = document.documentElement.lang || 'en-GB';
    if (locale === 'en') locale = 'en-GB';

    try {
      figure.textContent = new Intl.NumberFormat(locale).format(followers);
    } catch (e) {
      /* An unknown language tag: the plain digits stay. */
    }
  }

  /* ==========================================================================
     KEYBOARD ACCESS — a go-live blocker.

     Live Rebuy's drawer traps focus (checked by hand, 28 September), so ours
     without it would be a regression for keyboard and screen-reader users.
     theme.js's HeaderCart already does two things: Escape closes the drawer
     (a keyup on window while it is open) and a click outside closes it. The
     rest is here, behind the same body-class gate as everything above:

       - on open, focus goes to the close button;
       - Tab and Shift+Tab wrap inside the drawer;
       - the rest of the page is `inert` while it is open, so neither the
         keyboard nor a screen reader can reach it;
       - on close, focus goes back to whatever opened it;
       - when a rebuild removes the control that had keyboard focus, focus
         goes to its replacement rather than falling to <body>.

     theme.a11y.trapFocus is NOT reused: it reads the focusable list once,
     wraps only Shift+Tab from the first element, and switches off every other
     focusin handler on the page.

     Open and close are read off #HeaderCart's is-active class, because
     theme.js dispatches CartDrawer:open but nothing at all on close.
     ========================================================================== */

  var PANEL = '#HeaderCart';
  var TRIGGER = '#HeaderCartTrigger';

  /* Left interactive while the drawer is open. The backdrop is what a click
     outside lands on, and theme.js closes on that click. The cookie banner
     sits above the drawer on purpose (bc-cart-drawer.css) and must stay
     usable. */
  var INERT_EXEMPT = '.bc-drawer-backdrop, #cookies-banner';

  var TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]';

  var kb = {
    open: false,
    opener: null,     // what had focus when the drawer opened
    inerted: [],      // only what we set, so only that is undone
    last: null,       // where keyboard focus was, to put it back after a rebuild
    keyboard: false,  // last input was a key, not a pointer
    sentinel: null
  };

  function panelEl() { return document.querySelector(PANEL); }

  function focusQuietly(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function tabbables(scope) {
    return Array.prototype.filter.call(scope.querySelectorAll(TABBABLE), function (el) {
      if (el === kb.sentinel || el.tabIndex < 0) return false;
      if (el.closest('[inert], [hidden]')) return false;
      return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    });
  }

  /* Every sibling of every ancestor, up to <body>. A container that holds an
     exempt element is walked into rather than made inert whole. */
  function inertAround(panel) {
    kb.inerted = [];
    function mark(el) {
      if (/^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/.test(el.tagName)) return;
      if (el.hasAttribute('inert') || el.matches(INERT_EXEMPT)) return;
      if (el.querySelector(INERT_EXEMPT)) {
        Array.prototype.forEach.call(el.children, mark);
        return;
      }
      el.setAttribute('inert', '');
      kb.inerted.push(el);
    }
    for (var node = panel; node && node !== document.body && node.parentElement; node = node.parentElement) {
      Array.prototype.forEach.call(node.parentElement.children, function (sib) {
        if (sib !== node) mark(sib);
      });
    }
  }

  function clearInert() {
    kb.inerted.forEach(function (el) { el.removeAttribute('inert'); });
    kb.inerted = [];
  }

  /* The last stop in the tab order. Tabbing past the final control — which
     may sit inside a wallet button's shadow root, where no list of ours can
     see it — lands here and goes round to the first. */
  function ensureSentinel(panel) {
    if (kb.sentinel && kb.sentinel.parentNode === panel) return;
    var s = document.createElement('span');
    s.className = 'bc-focus-sentinel';
    s.tabIndex = 0;
    s.addEventListener('focus', function () { focusQuietly(tabbables(panel)[0]); });
    panel.appendChild(s);
    kb.sentinel = s;
  }

  function setExpanded(open) {
    var trigger = document.querySelector(TRIGGER);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* Where focus was, described so it can be found again in rebuilt markup:
     which row or FBT card, by position, and which control in it, by its
     stable classes (state classes like is-added are left out). */
  function remember(el) {
    var panel = panelEl();
    var anchor = el.closest('.cart__item, [data-bc-cart-fbt-card]');
    var anchorSel = anchor ? (anchor.matches('.cart__item') ? '[data-products] .cart__item' : '[data-bc-cart-fbt-card]') : null;
    var classes = Array.prototype.filter.call(el.classList, function (c) { return c.indexOf('is-') !== 0; });
    kb.last = {
      anchorSel: anchorSel,
      index: anchor ? Array.prototype.indexOf.call(panel.querySelectorAll(anchorSel), anchor) : -1,
      control: classes.length ? el.tagName.toLowerCase() + '.' + classes.map(function (c) {
        return window.CSS && CSS.escape ? CSS.escape(c) : c;
      }).join('.') : null
    };
  }

  function restoreFocus() {
    if (!kb.open || !kb.keyboard || !kb.last) return;
    var panel = panelEl();
    var active = document.activeElement;
    if (!panel || (active && active !== document.body)) return;

    var target = null;
    var d = kb.last;
    if (d.anchorSel) {
      var anchors = panel.querySelectorAll(d.anchorSel);
      if (anchors.length) {
        var a = anchors[Math.min(Math.max(d.index, 0), anchors.length - 1)];
        target = (d.control && a.querySelector(d.control)) || tabbables(a)[0] || null;
      }
    }
    focusQuietly(target || tabbables(panel)[0]);
  }

  /* ==========================================================================
     THE SUPPORT CHAT STEPS ASIDE WHILE THE DRAWER IS OPEN (12 October).

     Gorgias pins its launcher to the bottom right corner at z-index
     2147483636, which nothing of ours can be layered above: it sat on the
     Trustpilot line and the end of Checkout on a phone, and on the wallet
     buttons on desktop. It is already unusable there — the page behind the
     drawer is inert — so it is hidden on open and put back on close.

     THROUGH GORGIAS'S OWN METHOD, GorgiasChat.hideChat(true | false), which
     hides and shows the one element holding everything of theirs. Their
     markup is named here only to read whether that element is already hidden.

     ⚠ AN OPEN CONVERSATION IS HIDDEN WITH IT and comes back as it was, still
     open — hideChat() has no launcher-only form. On desktop an open chat
     window covers the same corner of the drawer.

     ⚠ ONLY WHAT THE DRAWER HID IS SHOWN AGAIN. If the chat was already hidden
     when the drawer opened, closing the drawer leaves it hidden.

     ⚠ GORGIAS ARRIVES LATE: its scripts are held back until the shopper's
     first interaction (layout/theme.liquid), and that interaction can be the
     one that opens the drawer. window.GorgiasChat does not exist yet then, so
     the drawer also listens for Gorgias's own `gorgias-widget-loaded` event
     on window — measured: it fires with the method and the element in place,
     about 50ms before the launcher is drawn — and hides the chat then if the
     drawer is still open.

     No Gorgias on the page, or a version without hideChat: nothing happens.
     ========================================================================== */

  var chatHiddenByDrawer = false;

  function setChatHidden(hide) {
    var chat = window.GorgiasChat;
    if (!chat || typeof chat.hideChat !== 'function') return;
    if (hide) {
      var box = document.getElementById('gorgias-chat-container');
      if (chatHiddenByDrawer || !box || box.style.display === 'none') return;
      chat.hideChat(true);
      chatHiddenByDrawer = true;
    } else if (chatHiddenByDrawer) {
      chat.hideChat(false);
      chatHiddenByDrawer = false;
    }
  }

  function onOpen(panel) {
    kb.open = true;
    setChatHidden(true);
    var active = document.activeElement;
    kb.opener = active && active !== document.body && !panel.contains(active) ? active : null;
    setExpanded(true);
    ensureSentinel(panel);
    inertAround(panel);
    focusQuietly(panel.querySelector('.bc-drawer__close') || tabbables(panel)[0]);
  }

  function onClose() {
    kb.open = false;
    kb.last = null;
    setChatHidden(false);
    clearInert();
    setExpanded(false);
    var back = kb.opener && document.contains(kb.opener) ? kb.opener : null;
    if (!back && kb.keyboard) back = document.querySelector(TRIGGER);
    kb.opener = null;
    focusQuietly(back);
  }

  function startKeyboard() {
    var panel = panelEl();
    if (!panel) return;

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    var title = panel.querySelector('.bc-drawer__title');
    if (title) {
      if (!title.id) title.id = 'BcDrawerTitle';
      panel.setAttribute('aria-labelledby', title.id);
    }
    setExpanded(false);

    document.addEventListener('keydown', function () { kb.keyboard = true; }, true);
    document.addEventListener('pointerdown', function () { kb.keyboard = false; }, true);

    /* Shift+Tab from the first control goes to the last; Tab from outside
       comes back in. Tab forwards off the end is the sentinel's job. */
    document.addEventListener('keydown', function (evt) {
      if (!kb.open || evt.key !== 'Tab') return;
      var list = tabbables(panel);
      if (!list.length) return;
      var active = document.activeElement;
      var inside = panel.contains(active);
      if (evt.shiftKey && (active === list[0] || !inside)) {
        evt.preventDefault();
        focusQuietly(list[list.length - 1]);
      } else if (!evt.shiftKey && !inside) {
        evt.preventDefault();
        focusQuietly(list[0]);
      }
    }, true);

    document.addEventListener('focusin', function (evt) {
      if (!kb.open) return;
      var t = evt.target;
      if (panel.contains(t)) {
        if (t !== kb.sentinel) remember(t);
        return;
      }
      if (t.closest && t.closest(INERT_EXEMPT)) return;
      focusQuietly(tabbables(panel)[0]);
    });

    /* theme.js replaces [data-products] on every rebuild and the FBT swaps its
       own innerHTML, so a focused stepper, bin or add button can be removed
       from under the keyboard. */
    new MutationObserver(restoreFocus).observe(panel, { childList: true, subtree: true });

    /* The chat loaded after the drawer opened — see setChatHidden(). */
    window.addEventListener('gorgias-widget-loaded', function () {
      if (kb.open) setChatHidden(true);
    });

    var wasOpen = panel.classList.contains('is-active');
    if (wasOpen) onOpen(panel);
    new MutationObserver(function () {
      var now = panel.classList.contains('is-active');
      if (now === wasOpen) return;
      wasOpen = now;
      if (now) onOpen(panel); else onClose();
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });
  }

  /* ==========================================================================
     STAGE 7 — a PARTIAL add is not a refusal.

     Asked for more than is in stock, Shopify adds what it can and still
     answers 422 ("Only 39 items were added to your cart due to
     availability."). theme.js treats every 422 as a refusal, so the cart
     moved while the drawer, its totals and the header count kept the old
     basket until the next page load (Stage 7, 28 September).

     So the real cart decides, never the wording: Shopify localises it, and
     English copy is not a machine key here. If the count moved, rebuild
     through theme.js (which also updates the header bubble), open the
     drawer, and show Shopify's message once, in it — the caller's
     clearButton removes the copy beside the button, so the shopper sees it
     in one place, not two. If it did not, this is a true refusal and
     today's behaviour stands: the message beside the button, and in the
     drawer only if it is already open.
     ========================================================================== */

  var pendingMessage = null;

  function cartRoute(path) {
    return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + path;
  }

  function shownCount() {
    var items = document.querySelector(ROOT + ' ' + PRODUCTS + ' ' + ITEMS);
    return items ? parseInt(items.getAttribute('data-count'), 10) : NaN;
  }

  function refusedAdd(message, clearButton) {
    if (!message) return;
    fetch(cartRoute('cart.js'), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var shown = shownCount();
        if (!cart || typeof cart.item_count !== 'number' || !isFinite(shown) || cart.item_count === shown) {
          showError(message);
          return;
        }
        pendingMessage = message;
        if (typeof clearButton === 'function') clearButton();
        document.dispatchEvent(new CustomEvent('cart:build'));
        var panel = document.getElementById('HeaderCart');
        if (!panel || !panel.classList.contains('is-active')) {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      })
      .catch(function () { showError(message); });
  }

  /* ---- the drawer's own message -------------------------------------- */

  function errorEl() { return document.querySelector('[data-bc-drawer-error]'); }

  function genericError() {
    return (window.theme && theme.strings && theme.strings.cartUpdateError) ||
      'Sorry, we could not update your basket. Please try again.';
  }

  function showError(message) {
    var el = errorEl();
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
  }

  function clearError() {
    var el = errorEl();
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /* For assets/bc-cart-fbt.js, whose own add is the main thing this reports,
     and for assets/pdp-test.js. Exposed rather than duplicated so there is one
     message surface with one set of rules about when it clears. refusedAdd is
     for any ADD that failed, so a partial one is caught wherever it began. */
  window.bcCartDrawer = {
    showError: showError,
    clearError: clearError,
    refusedAdd: refusedAdd
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
